
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { allArticles, mockUsers, User } from "@/utils/adminMockData";
import type { Article } from "@/types/article";

export function useAdminDashboardData() {
  const [searchTerm, setSearchTerm] = useState("");
  const [articles, setArticles] = useState<Article[]>(
    allArticles.map(a => ({ ...a, archived: false }))
  );
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; article: Article | null }>({ open: false, article: null });

  useEffect(() => {
    const fetchArticles = async () => {
      setLoadingArticles(true);
      try {
        const { data, error } = await supabase
          .from("articles")
          .select("*")
          .order("created_at", { ascending: false });

        if (!error && data && data.length > 0) {
          setArticles(
            data.map((article: any) => ({
              id: article.id,
              title: article.title,
              description: article.description || "No description available",
              category: article.category || "Uncategorized",
              premium: !!article.premium,
              readTime: article.read_time || "5 min read",
              date: article.date
                ? new Date(article.date).toLocaleDateString()
                : new Date().toLocaleDateString(),
              image:
                article.image ||
                "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d",
              status: article.status || "draft",
              author: article.author || "AI Team",
              views: article.views || 0,
              archived: false, // Default to false for fetched articles
            }))
          );
        } else {
          setArticles(allArticles.map(a => ({ ...a, archived: false })));
        }
      } catch (error) {
        console.error("Error fetching articles:", error);
        setArticles(allArticles.map(a => ({ ...a, archived: false })));
      }
      setLoadingArticles(false);
    };

    fetchArticles();
  }, []);

  // Filter non-archived articles
  const filteredArticles = articles.filter(
    article =>
      !article.archived &&
      (article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.author?.toLowerCase?.() ?? "").includes(searchTerm.toLowerCase()))
  );

  // Get only archived articles
  const archivedArticles = articles.filter(article => article.archived);

  // Filter archived articles based on search term
  const filteredArchivedArticles = archivedArticles.filter(
    article =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.author?.toLowerCase?.() ?? "").includes(searchTerm.toLowerCase())
  );

  const articleCount = articles.filter((article) => !article.archived).length;

  // Filter users
  const filteredUsers = mockUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.subscription.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleView = (id: number | string) => {
    window.location.assign(`/article/${id.toString()}`);
  };

  const handleEdit = (id: number | string) => {
    window.location.assign(`/admin/edit-article/${id.toString()}`);
  };

  const handleOpenDelete = (article: Article) => setDeleteDialog({ open: true, article });
  const handleCloseDelete = () => setDeleteDialog({ open: false, article: null });

  const handleDelete = async (id: number | string) => {
    setDeleteDialog({ open: false, article: null });
    toast.loading("Deleting article…");
    try {
      const articleId = id.toString();
      const { data: existingArticle, error: checkError } = await supabase
        .from("articles")
        .select("id")
        .eq("id", articleId)
        .maybeSingle();

      if (checkError || !existingArticle) {
        setArticles((prev) => prev.filter((a) => a.id !== id));
        toast.success("Article deleted.");
        return;
      }

      const { error } = await supabase
        .from("articles")
        .delete()
        .eq("id", articleId);

      if (error) {
        console.error("Error deleting article:", error);
        toast.error(`Failed to delete article: ${error.message}`);
      } else {
        setArticles((prev) => prev.filter((a) => a.id !== id));
        toast.success("Article deleted.");
      }
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Failed to delete article");
    }
  };

  const handleArchive = (id: number | string) => {
    setArticles((prev) =>
      prev.map(a =>
        a.id === id ? { ...a, archived: true } : a
      )
    );
    toast.success("Article archived.");
  };

  const handleUnarchive = (id: number | string) => {
    setArticles((prev) =>
      prev.map(a =>
        a.id === id ? { ...a, archived: false } : a
      )
    );
    toast.success("Article unarchived.");
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return {
    searchTerm,
    articles,
    filteredArticles,
    archivedArticles,
    filteredArchivedArticles,
    articleCount,
    loadingArticles,
    deleteDialog,
    handleView,
    handleEdit,
    handleOpenDelete,
    handleCloseDelete,
    handleDelete,
    handleArchive,
    handleUnarchive,
    handleSearch,
    filteredUsers,
  };
}
